/**
 * usePaymentRemindersV2 - Hook modernisé pour les relances de paiement
 *
 * Utilise le nouveau système ReminderPolicy (JSON levels)
 * Architecture: Hook → API → Backend → Prisma
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { remindersAPI, quoteRemindersAPI } from '../utils/api';
import { reminderPoliciesAPI, ReminderPolicy } from '../utils/reminderPoliciesAPI';
import { logger } from '../utils/logger';
import { notify } from '../stores/notificationStore';
import { useCompanyStore } from '../stores/companyStore';

// ==================== TYPES ====================

export interface InvoiceNeedingReminder {
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  clientEmail: string;
  total: number;
  dueDate: string;
  daysOverdue: number;
  reminderLevel: string;
  lastReminderDate: string | null;
}

export interface QuoteNeedingReminder {
  quoteId: string;
  quoteNumber: string;
  clientName: string;
  clientEmail: string;
  amountTotal: number;
  createdAt: string;
  expiryDate: string | null;
  daysSinceCreated: number;
  daysUntilExpiry: number | null;
  nextReminderType: string;
  lastReminderDate: string | null;
}

export interface ReminderHistory {
  id: string;
  reminderType: string;
  daysOverdue: number;
  amountDue: number;
  sentTo: string;
  emailSubject: string;
  sentAt: string;
  status: string;
}

export interface ReminderStatistics {
  totalReminders: number;
  uniqueInvoices: number;
  uniqueQuotes: number;
  firstReminders: number;
  secondReminders: number;
  thirdReminders: number;
  manualReminders: number;
  sentCount: number;
  failedCount: number;
  totalAmountReminded: number;
}

// ==================== HOOK ====================

export function usePaymentRemindersV2() {
  const queryClient = useQueryClient();
  const { company } = useCompanyStore();
  const companyId = company?.id || '';

  // Local state for history modal
  const [reminderHistory, setReminderHistory] = useState<ReminderHistory[]>([]);

  // ==================== QUERIES ====================

  // Invoices needing reminder
  const {
    data: invoices = [],
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['reminders', 'invoices', companyId],
    queryFn: async () => {
      const response = await remindersAPI.getInvoices();
      return (response.data || []) as InvoiceNeedingReminder[];
    },
    enabled: Boolean(companyId),
    staleTime: 30 * 1000,
  });

  // Quotes needing reminder
  const {
    data: quotes = [],
    isLoading: isLoadingQuotes,
    refetch: refetchQuotes,
  } = useQuery({
    queryKey: ['reminders', 'quotes', companyId],
    queryFn: async () => {
      const response = await quoteRemindersAPI.getQuotes();
      return (response.data || []) as QuoteNeedingReminder[];
    },
    enabled: Boolean(companyId),
    staleTime: 30 * 1000,
  });

  // Invoice statistics
  const {
    data: statistics,
    isLoading: isLoadingStats,
  } = useQuery({
    queryKey: ['reminders', 'statistics', 'invoice', companyId],
    queryFn: async () => {
      const response = await remindersAPI.getStatistics();
      return (response.data ?? response ?? null) as ReminderStatistics;
    },
    enabled: Boolean(companyId),
    staleTime: 60 * 1000,
  });

  // Quote statistics
  const {
    data: quoteStatistics,
    isLoading: isLoadingQuoteStats,
  } = useQuery({
    queryKey: ['reminders', 'statistics', 'quote', companyId],
    queryFn: async () => {
      const response = await quoteRemindersAPI.getStatistics();
      return (response.data ?? response ?? null) as ReminderStatistics;
    },
    enabled: Boolean(companyId),
    staleTime: 60 * 1000,
  });

  // Policies (pour afficher l'état actuel)
  const {
    data: policies = [],
    isLoading: isLoadingPolicies,
  } = useQuery({
    queryKey: ['reminder-policies', companyId],
    queryFn: async () => {
      const response = await reminderPoliciesAPI.getAll();
      return (response.data || []) as ReminderPolicy[];
    },
    enabled: Boolean(companyId),
    staleTime: 60 * 1000,
  });

  // ==================== MUTATIONS ====================

  // Send manual invoice reminder
  const sendManualInvoiceReminder = useMutation({
    mutationFn: async (invoiceId: string) => {
      return remindersAPI.sendManualInvoice(Number(invoiceId));
    },
    onSuccess: () => {
      notify.success('Relance envoyée avec succès');
      queryClient.invalidateQueries({ queryKey: ['reminders', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', 'statistics', 'invoice'] });
    },
    onError: (error: any) => {
      logger.error('sendManualInvoiceReminder error:', error);
      notify.error('Erreur lors de l\'envoi de la relance');
    },
  });

  // Send manual quote reminder
  const sendManualQuoteReminder = useMutation({
    mutationFn: async (quoteId: string) => {
      return quoteRemindersAPI.sendManual(Number(quoteId));
    },
    onSuccess: () => {
      notify.success('Relance envoyée avec succès');
      queryClient.invalidateQueries({ queryKey: ['reminders', 'quotes'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', 'statistics', 'quote'] });
    },
    onError: (error: any) => {
      logger.error('sendManualQuoteReminder error:', error);
      notify.error('Erreur lors de l\'envoi de la relance');
    },
  });

  // Process all invoice reminders
  const processInvoiceReminders = useMutation({
    mutationFn: async () => {
      return remindersAPI.processReminders();
    },
    onSuccess: (response) => {
      const sent = response.data?.data?.sent || 0;
      notify.success(`${sent} relance(s) envoyée(s)`);
      queryClient.invalidateQueries({ queryKey: ['reminders', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', 'statistics', 'invoice'] });
    },
    onError: (error: any) => {
      logger.error('processInvoiceReminders error:', error);
      notify.error('Erreur lors du traitement des relances');
    },
  });

  // Process all quote reminders
  const processQuoteReminders = useMutation({
    mutationFn: async () => {
      return quoteRemindersAPI.processReminders();
    },
    onSuccess: (response) => {
      const sent = response.data?.data?.sent || 0;
      notify.success(`${sent} relance(s) envoyée(s)`);
      queryClient.invalidateQueries({ queryKey: ['reminders', 'quotes'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', 'statistics', 'quote'] });
    },
    onError: (error: any) => {
      logger.error('processQuoteReminders error:', error);
      notify.error('Erreur lors du traitement des relances');
    },
  });

  // ==================== HISTORY HANDLERS ====================

  const loadInvoiceHistory = useCallback(async (invoiceId: string): Promise<boolean> => {
    try {
      const response = await remindersAPI.getHistory(Number(invoiceId));
      setReminderHistory(response.data?.data || response.data || []);
      return true;
    } catch (error) {
      logger.error('loadInvoiceHistory error:', error);
      setReminderHistory([]);
      return false;
    }
  }, []);

  const loadQuoteHistory = useCallback(async (quoteId: string): Promise<boolean> => {
    try {
      const response = await quoteRemindersAPI.getHistory(Number(quoteId));
      setReminderHistory(response.data?.data || response.data || []);
      return true;
    } catch (error) {
      logger.error('loadQuoteHistory error:', error);
      setReminderHistory([]);
      return false;
    }
  }, []);

  const clearHistory = useCallback(() => {
    setReminderHistory([]);
  }, []);

  // ==================== COMPUTED ====================

  const invoicePolicy = policies.find(p => p.entityType === 'invoice' && p.isActive);
  const quotePolicy = policies.find(p => p.entityType === 'quote' && p.isActive);

  const isLoading = isLoadingInvoices || isLoadingQuotes || isLoadingStats ||
                    isLoadingQuoteStats || isLoadingPolicies;

  const isProcessing = sendManualInvoiceReminder.isPending ||
                       sendManualQuoteReminder.isPending ||
                       processInvoiceReminders.isPending ||
                       processQuoteReminders.isPending;

  // ==================== RETURN ====================

  return {
    // Data
    invoices,
    quotes,
    statistics,
    quoteStatistics,
    reminderHistory,
    policies,
    invoicePolicy,
    quotePolicy,

    // Loading states
    isLoading,
    isProcessing,

    // Actions
    sendManualInvoiceReminder: (invoiceId: string) => sendManualInvoiceReminder.mutate(invoiceId),
    sendManualQuoteReminder: (quoteId: string) => sendManualQuoteReminder.mutate(quoteId),
    processInvoiceReminders: () => processInvoiceReminders.mutate(),
    processQuoteReminders: () => processQuoteReminders.mutate(),

    // History
    loadInvoiceHistory,
    loadQuoteHistory,
    clearHistory,

    // Refetch
    refetchInvoices,
    refetchQuotes,
  };
}

export default usePaymentRemindersV2;
